import React from 'react';
import { AlertTriangle, CheckSquare, Square } from 'lucide-react';

const gbp = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toFixed(2)}`);
const gbp4 = (v) => (v === null || v === undefined ? '—' : `£${Number(v).toFixed(4)}`);

const STATUS_LABEL = {
  fill: 'Destination cost missing — can fill',
  conflict: 'Destination already costed',
  identical: 'Already identical',
  missing_source: 'No source cost — nothing to copy',
  no_dest_row: 'No opening row in destination session — nothing written',
  no_dest_quantity: 'Destination quantity is 0 — nothing written',
  unit_mismatch: 'Unit differs — not converted, not written',
  multiple_dest_rows: 'Several opening rows for this product — not written',
};

const SOURCE_KIND = { completed_session: 'Last completed session', delivery: 'Delivery fallback' };

export function SourceLine({ src }) {
  if (!src) return <span className="text-slate-400">No source cost</span>;
  return (
    <span>
      {gbp4(src.unit_cost)} per <strong>{src.unit_type || src.raw_unit || 'unknown unit'}</strong>
      {' · '}{SOURCE_KIND[src.kind] || src.kind}: {src.ref}{src.date ? ` (${src.date})` : ''}
    </span>
  );
}

function CostRow({ it, selected, onToggle, overwrite }) {
  const d = it.dest;
  return (
    <div className="py-2 border-b border-slate-100 last:border-0 text-xs" data-testid={`cost-row-${it.product_id}`}>
      <div className="flex items-start gap-2">
        {it.writable ? (
          <input type="checkbox" className="mt-0.5" checked={selected} onChange={() => onToggle(it.product_id)}
            data-testid={`cost-select-${it.product_id}`} />
        ) : <span className="w-3.5" />}
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="font-medium text-slate-800">
            <span className="font-mono text-slate-400 mr-1">{it.product_code}</span>{it.product_name}
          </div>
          <div className="text-slate-500">Source: <SourceLine src={it.source} /></div>
          {d && (
            <div className="text-slate-500" data-testid={`cost-dest-${it.product_id}`}>
              Destination now: {d.quantity} {d.unit_type || d.unit_label} × {gbp4(d.unit_cost)} = {gbp(d.value)}
              {it.after && (
                <> → <strong className={it.writable ? 'text-slate-800' : 'font-normal text-slate-400 line-through'}>{d.quantity} × {gbp4(it.after.unit_cost)} = {gbp(it.after.value)}</strong>{!it.writable && ' (not applied)'}</>
              )}
            </div>
          )}
          <div className={it.status === 'conflict' ? 'text-amber-700' : it.writable ? 'text-emerald-700' : 'text-slate-400'}
            data-testid={`cost-status-${it.product_id}`}>
            {STATUS_LABEL[it.status] || it.status}
            {it.status === 'conflict' && (overwrite ? ' — tick to overwrite' : ' — preserved (overwrite is off)')}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CostCopySection({ costs, selected, onToggle, onSetAll, overwrite }) {
  if (costs.needs_session) {
    return (
      <p className="text-xs text-amber-700" data-testid="cost-needs-session">
        Choose a destination session above to preview opening-stock costs.
      </p>
    );
  }
  const writable = costs.items.filter(i => i.writable).map(i => i.product_id);
  return (
    <div className="space-y-2" data-testid="cost-copy-section">
      <p className="text-xs text-slate-500">
        Destination session <strong>{costs.dest_session.reference}</strong> ({costs.dest_session.status}, {costs.dest_session.session_date}).
        {' '}Source: {costs.source_session
          ? <>last completed session <strong>{costs.source_session.reference}</strong> ({costs.source_session.session_date}), delivery fallback where the product is not in it.</>
          : <>no completed session at source — latest posted delivery only.</>}
        {' '}Costs are copied per their own unit; nothing is converted.
      </p>
      {costs.populated_dest_count > 0 && (
        <div className="flex gap-2 items-start rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
          data-testid="cost-populated-warning">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            This destination session already has <strong>{costs.populated_dest_count}</strong> costed opening row
            {costs.populated_dest_count !== 1 ? 's' : ''} (total {gbp(costs.populated_dest_value)}).
            {overwrite ? ' Overwrite is ON — only rows you tick below will be replaced.' : ' Overwrite is OFF — they will be kept as they are.'}
          </span>
        </div>
      )}
      {writable.length > 0 && (
        <div className="flex gap-3 text-xs">
          <button type="button" className="flex items-center gap-1 text-slate-700 hover:text-slate-900"
            onClick={() => onSetAll(writable)} data-testid="cost-select-all">
            <CheckSquare className="h-3.5 w-3.5" /> Select all costable ({writable.length})
          </button>
          <button type="button" className="flex items-center gap-1 text-slate-500 hover:text-slate-700"
            onClick={() => onSetAll([])} data-testid="cost-deselect-all">
            <Square className="h-3.5 w-3.5" /> Deselect all
          </button>
          <span className="text-slate-400" data-testid="cost-selected-count">{selected.size} selected</span>
        </div>
      )}
      <div className="max-h-96 overflow-y-auto border border-slate-200 rounded px-2">
        {costs.items.length === 0 && <p className="text-xs text-slate-400 py-2">No products to cost.</p>}
        {costs.items.map(it => (
          <CostRow key={it.product_id} it={it} selected={selected.has(it.product_id)} onToggle={onToggle} overwrite={overwrite} />
        ))}
      </div>
    </div>
  );
}
